import { useEffect, useRef, useState } from 'react'

/** Characters per frame grow with the backlog, so a long burst (a held reply released at once) still lands in ~0.6s. */
const FRAMES_TO_CATCH_UP = 36

/**
 * Reveals `text` at an even pace as it streams in, instead of in the uneven chunks the network delivers.
 * `animate` false (a conversation loaded from history) shows the text at once.
 */
export const useSmoothText = (text: string, animate: boolean) => {
  const [shown, setShown] = useState(animate ? 0 : text.length)
  const shownRef = useRef(shown)
  const textRef = useRef(text)
  textRef.current = text

  useEffect(() => {
    if (!animate) {
      shownRef.current = text.length
      setShown(text.length)
      return
    }
    if (shownRef.current >= text.length) return
    let frame = 0
    const step = () => {
      const target = textRef.current.length
      const backlog = target - shownRef.current
      if (backlog <= 0) return
      shownRef.current = Math.min(target, shownRef.current + Math.max(1, Math.ceil(backlog / FRAMES_TO_CATCH_UP)))
      setShown(shownRef.current)
      frame = requestAnimationFrame(step)
    }
    frame = requestAnimationFrame(step)
    return () => cancelAnimationFrame(frame)
  }, [text, animate])

  return { text: text.slice(0, shown), done: shown >= text.length }
}
